/**
 * R128: Talent trees scaffolding (WotLK 3.3.5a pattern: 3 branches × 7 tiers × class).
 *
 * Each class gets 3 spec branches with 7 tier rows. Each tier has 1-3 talents.
 * Talents are mostly +X% effectiveness or +Y% chance for an existing ability.
 *
 * Vanilla canon: 51 talent points at lvl 60 (1 per level after 9).
 * AIWARI plan: same — 1 talent point per level past 9.
 */

export interface Talent {
  id: string;
  name: string;
  description: string;
  maxRanks: number;
  tier: number;       // 0-6 (7 tiers)
  column: number;     // 0-3 within branch
  branch: TalentBranch;
  /** Effect per rank: array of strings explaining what happens at each rank. */
  effectPerRank: string[];
  /** Pre-req talent (must be maxed first). */
  requires?: string;
  /** Adds modifiers to formulas at runtime. */
  modifiers?: Array<{
    stat: 'crit_pct' | 'haste_pct' | 'dmg_pct' | 'mana_cost' | 'cooldown_ms' | 'spell_pen' | 'armor_pct' | 'dodge_pct' | 'hp_pct';
    perRank: number;
  }>;
}

export type TalentBranch = string; // e.g. 'arms' | 'fury' | 'protection' for warrior

export interface ClassTalentTree {
  classId: string;
  branches: Record<string, { name: string; talents: Talent[] }>;
}

// ── Talent tree templates per class ──────────────────────────────────

function makeTalent(o: Partial<Talent> & { id: string; name: string; tier: number; column: number; branch: string }): Talent {
  return {
    description: o.description ?? `Improves ${o.name}.`,
    maxRanks: o.maxRanks ?? 5,
    effectPerRank: o.effectPerRank ?? Array.from({ length: o.maxRanks ?? 5 }, (_, i) => `Rank ${i+1}: +${(i+1) * 2}% effect`),
    modifiers: o.modifiers ?? [],
    requires: o.requires,
    id: o.id, name: o.name, tier: o.tier, column: o.column, branch: o.branch,
  } as Talent;
}

// ── Warrior ─────────────────────────────────────────────────────────
export const warriorTree: ClassTalentTree = {
  classId: 'warrior',
  branches: {
    arms: {
      name: 'Оружие',  // Two-handed weapon focus, big slow hits
      talents: [
        makeTalent({ id: 'arms-improved-strike', name: 'Усиленный удар', tier: 0, column: 0, branch: 'arms', maxRanks: 5,
          effectPerRank: ['+2% урона двуручным', '+4% урона', '+6% урона', '+8% урона', '+10% урона'],
          modifiers: [{ stat: 'dmg_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'arms-deflection', name: 'Отражение', tier: 0, column: 1, branch: 'arms', maxRanks: 5,
          modifiers: [{ stat: 'dodge_pct', perRank: 0.01 }] }),
        makeTalent({ id: 'arms-improved-charge', name: 'Усиленный рывок', tier: 1, column: 0, branch: 'arms', maxRanks: 2 }),
        makeTalent({ id: 'arms-deep-wounds', name: 'Глубокие раны', tier: 2, column: 1, branch: 'arms', maxRanks: 3,
          description: 'Критические удары наносят дополнительный урон от кровотечения в течение 12 сек.' }),
        makeTalent({ id: 'arms-impale', name: 'Пробивание', tier: 3, column: 1, branch: 'arms', maxRanks: 2,
          description: 'Увеличивает урон критических ударов ещё на 10/20%.',
          modifiers: [{ stat: 'crit_pct', perRank: 0.10 }] }),
        makeTalent({ id: 'arms-mortal-strike', name: 'Смертельный удар', tier: 6, column: 1, branch: 'arms', maxRanks: 1,
          description: 'Жестокий удар, наносящий урон оружием и снижающий лечение цели на 50% на 10 сек.' }),
      ],
    },
    fury: {
      name: 'Ярость',  // Dual-wield, fast hits, anger
      talents: [
        makeTalent({ id: 'fury-improved-bloodrage', name: 'Усиленная кровавая ярость', tier: 0, column: 1, branch: 'fury', maxRanks: 2 }),
        makeTalent({ id: 'fury-cruelty', name: 'Жестокость', tier: 0, column: 2, branch: 'fury', maxRanks: 5,
          description: 'Увеличивает шанс критического удара ближнего боя на 1/2/3/4/5%.',
          modifiers: [{ stat: 'crit_pct', perRank: 0.01 }] }),
        makeTalent({ id: 'fury-flurry', name: 'Шквал', tier: 5, column: 1, branch: 'fury', maxRanks: 5,
          description: 'После крита следующие 3 удара получают +30% скорости атаки.' }),
        makeTalent({ id: 'fury-bloodthirst', name: 'Кровожадность', tier: 6, column: 1, branch: 'fury', maxRanks: 1,
          description: 'Мгновенный удар на 45% силы атаки. Восстанавливает 1% макс. HP.' }),
      ],
    },
    protection: {
      name: 'Защита',  // Tanking, shield, defensive
      talents: [
        makeTalent({ id: 'prot-shield-spec', name: 'Специализация щита', tier: 0, column: 2, branch: 'protection', maxRanks: 5 }),
        makeTalent({ id: 'prot-toughness', name: 'Стойкость', tier: 1, column: 1, branch: 'protection', maxRanks: 5,
          description: 'Увеличивает броню предметов на 2/4/6/8/10%.',
          modifiers: [{ stat: 'armor_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'prot-defiance', name: 'Непокорность', tier: 2, column: 1, branch: 'protection', maxRanks: 3 }),
        makeTalent({ id: 'prot-shield-wall', name: 'Стена щитов', tier: 6, column: 1, branch: 'protection', maxRanks: 1,
          description: 'Снижает весь получаемый урон на 75% на 10 сек. Кулдаун 30 мин.' }),
      ],
    },
  },
};

// ── Mage ────────────────────────────────────────────────────────────
export const mageTree: ClassTalentTree = {
  classId: 'mage',
  branches: {
    arcane: {
      name: 'Магия',
      talents: [
        makeTalent({ id: 'arc-subtlety', name: 'Утончённость', tier: 0, column: 0, branch: 'arcane', maxRanks: 5 }),
        makeTalent({ id: 'arc-clearcasting', name: 'Чистое колдовство', tier: 2, column: 1, branch: 'arcane', maxRanks: 3,
          description: 'Криты дают 33/66/100% шанс что следующее заклинание не потратит ману.' }),
        makeTalent({ id: 'arc-presence-of-mind', name: 'Присутствие духа', tier: 6, column: 1, branch: 'arcane', maxRanks: 1,
          description: 'Следующее заклинание в течение 10с становится мгновенным. Кулдаун 3 мин.' }),
      ],
    },
    fire: {
      name: 'Огонь',
      talents: [
        makeTalent({ id: 'fire-improved-fireball', name: 'Усиленный огненный шар', tier: 0, column: 1, branch: 'fire', maxRanks: 5,
          description: 'Сокращает время каста огненного шара на 0.1/0.2/0.3/0.4/0.5 сек.' }),
        makeTalent({ id: 'fire-ignite', name: 'Возгорание', tier: 1, column: 1, branch: 'fire', maxRanks: 5,
          description: 'Криты вызывают дополнительный 8/16/24/32/40% урон от горения за 4 сек.' }),
        makeTalent({ id: 'fire-pyroblast', name: 'Пироблик', tier: 6, column: 1, branch: 'fire', maxRanks: 1,
          description: 'Каст 6 сек. Огромный огненный урон + периодический урон.' }),
      ],
    },
    frost: {
      name: 'Лёд',
      talents: [
        makeTalent({ id: 'frost-improved-frostbolt', name: 'Усиленная ледяная стрела', tier: 0, column: 1, branch: 'frost', maxRanks: 5 }),
        makeTalent({ id: 'frost-elemental-precision', name: 'Стихийная точность', tier: 1, column: 1, branch: 'frost', maxRanks: 3 }),
        makeTalent({ id: 'frost-ice-block', name: 'Ледяная глыба', tier: 6, column: 1, branch: 'frost', maxRanks: 1,
          description: 'Иммунитет на 10 сек. Кулдаун 5 мин.' }),
      ],
    },
  },
};

// ── R179: Vanilla-flavour talent trees for the remaining 7 classes.
// Каждое дерево = 3 ветки × 4-6 talents × 5 рангов (или 1 для capstone).

// Paladin: Holy / Protection / Retribution.
export const paladinTree: ClassTalentTree = {
  classId: 'paladin',
  branches: {
    holy: {
      name: 'Свет',
      talents: [
        makeTalent({ id: 'pal-holy-improved-flash', name: 'Усиленный быстрый Свет', tier: 0, column: 1, branch: 'holy', maxRanks: 5,
          description: 'Сокращает время Flash of Light на 0.1/0.2/0.3/0.4/0.5с.',
          modifiers: [{ stat: 'haste_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'pal-holy-divine-strength', name: 'Сила Света', tier: 1, column: 0, branch: 'holy', maxRanks: 5,
          description: 'Увеличивает силу заклинаний на +2/4/6/8/10%.',
          modifiers: [{ stat: 'dmg_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'pal-holy-illumination', name: 'Озарение', tier: 3, column: 1, branch: 'holy', maxRanks: 5,
          description: 'При крите вы возвращаете 20/40/60/80/100% маны.' }),
        makeTalent({ id: 'pal-holy-divine-favor', name: 'Благосклонность', tier: 6, column: 1, branch: 'holy', maxRanks: 1,
          description: 'Следующий каст → 100% крит. 2 мин cd.' }),
      ],
    },
    protection: {
      name: 'Защита',
      talents: [
        makeTalent({ id: 'pal-prot-deflection', name: 'Отражение', tier: 0, column: 0, branch: 'protection', maxRanks: 5,
          description: '+1/2/3/4/5% к парированию.',
          modifiers: [{ stat: 'dodge_pct', perRank: 1.0 }] }),
        makeTalent({ id: 'pal-prot-anticipation', name: 'Предвидение', tier: 1, column: 1, branch: 'protection', maxRanks: 5,
          description: '+2/4/6/8/10% к уклонению.',
          modifiers: [{ stat: 'dodge_pct', perRank: 2.0 }] }),
        makeTalent({ id: 'pal-prot-redoubt', name: 'Усиленная защита', tier: 4, column: 1, branch: 'protection', maxRanks: 5 }),
        makeTalent({ id: 'pal-prot-holy-shield', name: 'Святой щит', tier: 6, column: 1, branch: 'protection', maxRanks: 1,
          description: 'На 10с блокирует следующие 4 удара. Каждый блок = ущерб от Света.' }),
      ],
    },
    retribution: {
      name: 'Возмездие',
      talents: [
        makeTalent({ id: 'pal-ret-benediction', name: 'Благословение', tier: 0, column: 1, branch: 'retribution', maxRanks: 5,
          description: 'Снижает стоимость seals на 3/6/9/12/15%.',
          modifiers: [{ stat: 'mana_cost', perRank: -0.03 }] }),
        makeTalent({ id: 'pal-ret-vengeance', name: 'Возмездие', tier: 1, column: 1, branch: 'retribution', maxRanks: 5,
          description: '+2/4/6/8/10% к Holy / Fire / Frost урону.',
          modifiers: [{ stat: 'dmg_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'pal-ret-sanctity-aura', name: 'Аура святости', tier: 4, column: 1, branch: 'retribution', maxRanks: 1 }),
        makeTalent({ id: 'pal-ret-crusader-strike', name: 'Удар крестоносца', tier: 6, column: 1, branch: 'retribution', maxRanks: 1,
          description: 'Мощный мгновенный удар, 6с cd.' }),
      ],
    },
  },
};

// Cleric (Priest): Discipline / Holy / Shadow.
export const clericTree: ClassTalentTree = {
  classId: 'cleric',
  branches: {
    discipline: {
      name: 'Послушание',
      talents: [
        makeTalent({ id: 'cle-disc-unbreakable-will', name: 'Несгибаемая воля', tier: 0, column: 0, branch: 'discipline', maxRanks: 5 }),
        makeTalent({ id: 'cle-disc-mental-strength', name: 'Сила разума', tier: 2, column: 1, branch: 'discipline', maxRanks: 5,
          description: '+2/4/6/8/10% к максимальной мане.' }),
        makeTalent({ id: 'cle-disc-power-infusion', name: 'Прилив сил', tier: 6, column: 1, branch: 'discipline', maxRanks: 1,
          description: 'Цель получает +20% к скорости каста и -20% к стоимости маны на 15с.' }),
      ],
    },
    holy: {
      name: 'Свет',
      talents: [
        makeTalent({ id: 'cle-holy-improved-renew', name: 'Усиленное Обновление', tier: 0, column: 1, branch: 'holy', maxRanks: 3,
          description: '+5/10/15% к лечению заклинания «Обновление».' }),
        makeTalent({ id: 'cle-holy-spiritual-guidance', name: 'Духовное руководство', tier: 4, column: 1, branch: 'holy', maxRanks: 5 }),
        makeTalent({ id: 'cle-holy-circle-of-healing', name: 'Круг исцеления', tier: 6, column: 1, branch: 'holy', maxRanks: 1,
          description: 'Лечит 5 ближайших союзников.' }),
      ],
    },
    shadow: {
      name: 'Тень',
      talents: [
        makeTalent({ id: 'cle-shadow-darkness', name: 'Тьма', tier: 0, column: 1, branch: 'shadow', maxRanks: 5,
          description: '+2/4/6/8/10% к урону Тенью.',
          modifiers: [{ stat: 'dmg_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'cle-shadow-mind-flay', name: 'Бичевание разума', tier: 4, column: 1, branch: 'shadow', maxRanks: 1,
          description: 'Канал-урон 3 раза за 3 сек + замедление.' }),
        makeTalent({ id: 'cle-shadow-shadowform', name: 'Облик Тени', tier: 6, column: 1, branch: 'shadow', maxRanks: 1,
          description: '+15% к урону Тенью, но запрещает лечение.' }),
      ],
    },
  },
};

// Ranger (Hunter): Beast Mastery / Marksmanship / Survival.
export const rangerTree: ClassTalentTree = {
  classId: 'ranger',
  branches: {
    beastMastery: {
      name: 'Повелитель зверей',
      talents: [
        makeTalent({ id: 'rng-bm-improved-aspect-of-monkey', name: 'Усиленный облик мартышки', tier: 0, column: 1, branch: 'beastMastery', maxRanks: 3 }),
        makeTalent({ id: 'rng-bm-unleashed-fury', name: 'Развязанная ярость', tier: 3, column: 1, branch: 'beastMastery', maxRanks: 5,
          description: '+4/8/12/16/20% урона питомца.' }),
        makeTalent({ id: 'rng-bm-bestial-wrath', name: 'Звериный гнев', tier: 6, column: 1, branch: 'beastMastery', maxRanks: 1,
          description: 'Питомец на 18 сек получает +50% урона и иммунитет к контролю.' }),
      ],
    },
    marksmanship: {
      name: 'Стрельба',
      talents: [
        makeTalent({ id: 'rng-mm-lethal-shots', name: 'Смертельные выстрелы', tier: 0, column: 1, branch: 'marksmanship', maxRanks: 5,
          description: '+1/2/3/4/5% крит к выстрелам.',
          modifiers: [{ stat: 'crit_pct', perRank: 1.0 }] }),
        makeTalent({ id: 'rng-mm-aimed-shot', name: 'Прицельный выстрел', tier: 4, column: 1, branch: 'marksmanship', maxRanks: 1,
          description: '3с каст, мощный одиночный выстрел.' }),
        makeTalent({ id: 'rng-mm-trueshot-aura', name: 'Аура меткости', tier: 6, column: 1, branch: 'marksmanship', maxRanks: 1,
          description: 'Группа получает +25 силы дальнего боя.' }),
      ],
    },
    survival: {
      name: 'Выживание',
      talents: [
        makeTalent({ id: 'rng-surv-deflection', name: 'Отражение', tier: 0, column: 0, branch: 'survival', maxRanks: 5,
          modifiers: [{ stat: 'dodge_pct', perRank: 1.0 }] }),
        makeTalent({ id: 'rng-surv-clever-traps', name: 'Хитрые ловушки', tier: 3, column: 1, branch: 'survival', maxRanks: 2 }),
        makeTalent({ id: 'rng-surv-counterattack', name: 'Контратака', tier: 6, column: 1, branch: 'survival', maxRanks: 1,
          description: 'После парирования — мощная контратака.' }),
      ],
    },
  },
};

// Rogue: Assassination / Combat / Subtlety.
export const rogueTree: ClassTalentTree = {
  classId: 'rogue',
  branches: {
    assassination: {
      name: 'Ликвидация',
      talents: [
        makeTalent({ id: 'rog-ass-improved-eviscerate', name: 'Усиленное потрошение', tier: 0, column: 1, branch: 'assassination', maxRanks: 3,
          description: '+5/10/15% урона «Потрошения».',
          modifiers: [{ stat: 'dmg_pct', perRank: 0.05 }] }),
        makeTalent({ id: 'rog-ass-malice', name: 'Злоба', tier: 1, column: 1, branch: 'assassination', maxRanks: 5,
          description: '+1/2/3/4/5% к шансу крита.',
          modifiers: [{ stat: 'crit_pct', perRank: 1.0 }] }),
        makeTalent({ id: 'rog-ass-cold-blood', name: 'Холодная кровь', tier: 6, column: 1, branch: 'assassination', maxRanks: 1,
          description: 'Следующий мгновенный удар → 100% крит.' }),
      ],
    },
    combat: {
      name: 'Бой',
      talents: [
        makeTalent({ id: 'rog-cmb-improved-sinister', name: 'Усиленный Зловещий удар', tier: 0, column: 1, branch: 'combat', maxRanks: 5,
          description: 'Снижает затраты энергии «Зловещего удара» на 1/2/3/4/5.',
          modifiers: [{ stat: 'mana_cost', perRank: -0.05 }] }),
        makeTalent({ id: 'rog-cmb-aggression', name: 'Агрессия', tier: 4, column: 1, branch: 'combat', maxRanks: 3 }),
        makeTalent({ id: 'rog-cmb-adrenaline-rush', name: 'Прилив адреналина', tier: 6, column: 1, branch: 'combat', maxRanks: 1,
          description: 'Удваивает регенерацию энергии на 15 сек.' }),
      ],
    },
    subtlety: {
      name: 'Скрытность',
      talents: [
        makeTalent({ id: 'rog-sub-master-of-deception', name: 'Мастер обмана', tier: 0, column: 1, branch: 'subtlety', maxRanks: 5 }),
        makeTalent({ id: 'rog-sub-sinister-improved-backstab', name: 'Усиленный удар в спину', tier: 2, column: 1, branch: 'subtlety', maxRanks: 3,
          description: '+10/20/30% крит к «Удару в спину».' }),
        makeTalent({ id: 'rog-sub-premeditation', name: 'Заблаговременная подготовка', tier: 6, column: 1, branch: 'subtlety', maxRanks: 1,
          description: '+2 серии комбо, кулдаун 1 мин.' }),
      ],
    },
  },
};

// Shaman: Elemental / Enhancement / Restoration.
export const shamanTree: ClassTalentTree = {
  classId: 'shaman',
  branches: {
    elemental: {
      name: 'Стихии',
      talents: [
        makeTalent({ id: 'sha-ele-convection', name: 'Конвекция', tier: 0, column: 1, branch: 'elemental', maxRanks: 5,
          description: 'Снижает стоимость стихийных заклинаний на 2/4/6/8/10%.',
          modifiers: [{ stat: 'mana_cost', perRank: -0.02 }] }),
        makeTalent({ id: 'sha-ele-call-of-thunder', name: 'Зов грома', tier: 4, column: 1, branch: 'elemental', maxRanks: 5,
          description: '+1/2/3/4/5% крит к молнии.',
          modifiers: [{ stat: 'crit_pct', perRank: 1.0 }] }),
        makeTalent({ id: 'sha-ele-elemental-mastery', name: 'Стихийное мастерство', tier: 6, column: 1, branch: 'elemental', maxRanks: 1,
          description: 'Следующий стихийный каст бесплатен и крит.' }),
      ],
    },
    enhancement: {
      name: 'Усиление',
      talents: [
        makeTalent({ id: 'sha-enh-anticipation', name: 'Предвидение', tier: 0, column: 1, branch: 'enhancement', maxRanks: 5,
          modifiers: [{ stat: 'dodge_pct', perRank: 1.0 }] }),
        makeTalent({ id: 'sha-enh-flurry', name: 'Шквал', tier: 4, column: 1, branch: 'enhancement', maxRanks: 5,
          description: '+10/20/30/40/50% скорости атаки на 3 атаки после крита.',
          modifiers: [{ stat: 'haste_pct', perRank: 0.10 }] }),
        makeTalent({ id: 'sha-enh-stormstrike', name: 'Удар Бури', tier: 6, column: 1, branch: 'enhancement', maxRanks: 1,
          description: '2 удара + повышенный шоковый урон цели на 12 сек.' }),
      ],
    },
    restoration: {
      name: 'Исцеление',
      talents: [
        makeTalent({ id: 'sha-rest-tidal-focus', name: 'Океаническая концентрация', tier: 0, column: 1, branch: 'restoration', maxRanks: 5,
          description: 'Снижает ману «Волны исцеления» на 1/2/3/4/5%.',
          modifiers: [{ stat: 'mana_cost', perRank: -0.01 }] }),
        makeTalent({ id: 'sha-rest-purification', name: 'Очищение', tier: 3, column: 1, branch: 'restoration', maxRanks: 5 }),
        makeTalent({ id: 'sha-rest-natures-swiftness', name: 'Природная стремительность', tier: 6, column: 1, branch: 'restoration', maxRanks: 1,
          description: 'Следующее природное заклинание становится мгновенным.' }),
      ],
    },
  },
};

// Warlock: Affliction / Demonology / Destruction.
export const warlockTree: ClassTalentTree = {
  classId: 'warlock',
  branches: {
    affliction: {
      name: 'Колдовство',
      talents: [
        makeTalent({ id: 'wlk-aff-suppression', name: 'Подавление', tier: 0, column: 1, branch: 'affliction', maxRanks: 5,
          description: 'Снижает шанс сопротивления заклинаний колдовства.' }),
        makeTalent({ id: 'wlk-aff-improved-corruption', name: 'Усиленная Скверна', tier: 1, column: 1, branch: 'affliction', maxRanks: 5,
          description: 'Снижает время каста «Скверны» на 0.4/0.8/1.2/1.6/2.0 сек.',
          modifiers: [{ stat: 'haste_pct', perRank: 0.05 }] }),
        makeTalent({ id: 'wlk-aff-curse-of-doom', name: 'Проклятие судьбы', tier: 6, column: 1, branch: 'affliction', maxRanks: 1,
          description: 'Огромный фиксированный периодический урон на 60 сек. Кулдаун 1 мин.' }),
      ],
    },
    demonology: {
      name: 'Демонология',
      talents: [
        makeTalent({ id: 'wlk-dem-improved-imp', name: 'Усиленный Бес', tier: 0, column: 1, branch: 'demonology', maxRanks: 3 }),
        makeTalent({ id: 'wlk-dem-master-summoner', name: 'Мастер призыва', tier: 3, column: 1, branch: 'demonology', maxRanks: 2 }),
        makeTalent({ id: 'wlk-dem-summon-felguard', name: 'Призыв Стража Скверны', tier: 6, column: 1, branch: 'demonology', maxRanks: 1,
          description: 'Призывает мощного демонического воина как питомца.' }),
      ],
    },
    destruction: {
      name: 'Разрушение',
      talents: [
        makeTalent({ id: 'wlk-des-improved-shadow-bolt', name: 'Усиленная Тень', tier: 0, column: 1, branch: 'destruction', maxRanks: 5,
          description: '+1/2/3/4/5% крит к «Тёмной стреле».',
          modifiers: [{ stat: 'crit_pct', perRank: 1.0 }] }),
        makeTalent({ id: 'wlk-des-cataclysm', name: 'Катаклизм', tier: 1, column: 1, branch: 'destruction', maxRanks: 5,
          description: 'Снижает ману заклинаний школы Разрушения.',
          modifiers: [{ stat: 'mana_cost', perRank: -0.01 }] }),
        makeTalent({ id: 'wlk-des-shadowburn', name: 'Огонь тени', tier: 6, column: 1, branch: 'destruction', maxRanks: 1,
          description: 'Мгновенный взрыв Тени. Возвращает осколок души при убийстве.' }),
      ],
    },
  },
};

// Druid: Balance / Feral / Restoration.
export const druidTree: ClassTalentTree = {
  classId: 'druid',
  branches: {
    balance: {
      name: 'Баланс',
      talents: [
        makeTalent({ id: 'dru-bal-improved-wrath', name: 'Усиленный Гнев', tier: 0, column: 1, branch: 'balance', maxRanks: 5,
          description: 'Снижает время каста «Гнева» на 0.1/0.2/0.3/0.4/0.5 сек.',
          modifiers: [{ stat: 'haste_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'dru-bal-natures-grasp', name: 'Хватка природы', tier: 1, column: 1, branch: 'balance', maxRanks: 1,
          description: 'Корни любого, кто атакует тебя в ближнем бою. Кулдаун 1 мин.' }),
        makeTalent({ id: 'dru-bal-moonkin-form', name: 'Облик лунного совоморфа', tier: 6, column: 1, branch: 'balance', maxRanks: 1,
          description: '+10% к шансу крита заклинаний для группы.' }),
      ],
    },
    feral: {
      name: 'Свирепость',
      talents: [
        makeTalent({ id: 'dru-fer-ferocity', name: 'Жестокость', tier: 0, column: 1, branch: 'feral', maxRanks: 5,
          description: 'Снижает энергозатраты способностей в облике кошки на 1/2/3/4/5.',
          modifiers: [{ stat: 'mana_cost', perRank: -0.05 }] }),
        makeTalent({ id: 'dru-fer-thick-hide', name: 'Толстая шкура', tier: 1, column: 1, branch: 'feral', maxRanks: 5,
          description: '+2/4/6/8/10% к броне.',
          modifiers: [{ stat: 'armor_pct', perRank: 0.02 }] }),
        makeTalent({ id: 'dru-fer-leader-of-the-pack', name: 'Вожак стаи', tier: 6, column: 1, branch: 'feral', maxRanks: 1,
          description: 'Аура +3% к криту ближнего боя для группы в обликах кошки/медведя.' }),
      ],
    },
    restoration: {
      name: 'Исцеление',
      talents: [
        makeTalent({ id: 'dru-res-improved-mark', name: 'Усиленная Метка природы', tier: 0, column: 1, branch: 'restoration', maxRanks: 5 }),
        makeTalent({ id: 'dru-res-tranquil-spirit', name: 'Спокойный дух', tier: 3, column: 1, branch: 'restoration', maxRanks: 5 }),
        makeTalent({ id: 'dru-res-swiftmend', name: 'Скоротечное исцеление', tier: 6, column: 1, branch: 'restoration', maxRanks: 1,
          description: 'Мгновенно «сжигает» один периодический эффект исцеления в крупное исцеление.' }),
      ],
    },
  },
};

export const TALENT_TREES: Record<string, ClassTalentTree> = {
  warrior: warriorTree,
  mage: mageTree,
  paladin: paladinTree,
  cleric:  clericTree,
  ranger:  rangerTree,
  rogue:   rogueTree,
  shaman:  shamanTree,
  warlock: warlockTree,
  druid:   druidTree,
};

/** Compute total talent points spent in a branch from a points map. */
export function pointsInBranch(treeId: string, branchKey: string, allocation: Record<string, number>): number {
  const tree = TALENT_TREES[treeId];
  if (!tree) return 0;
  const branch = tree.branches[branchKey];
  if (!branch) return 0;
  let sum = 0;
  for (const t of branch.talents) sum += allocation[t.id] ?? 0;
  return sum;
}

/** Available points at level. Vanilla: 1 per level past 9 → max 51 at 60. */
export function availablePoints(level: number, spent: number): number {
  return Math.max(0, level - 9) - spent;
}
